import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GuardsCheckEnd, NavigationEnd, Router } from '@angular/router';
import { AppConfigProvider } from '@app/app.config.provider';
import { ExportALMConfigComponent } from '@components/export-alm/export-alm-config/export-alm-config.component';
import { ExportConfigComponent } from '@components/export/export-config/export-config.component';
import { exportDialogSizes } from '@components/export/export.config';
import { IAppConfigModules } from '@models/appconfig';
import { ExportAlmMethod } from '@models/export-alm/export-alm-method';
import { IExportAlmRequest } from '@models/export-alm/export-alm-request';
import { IExportAlmNonMaturityResponse, IExportAlmVintageResponse } from '@models/export-alm/export-alm-response';
import { IImportSettings } from '@models/import/import-settings';
import { IMenuOption, IProductMenuOption, MenuOptions } from '@models/menu/options';
import { allProductsItem, applyProductSortingRules, IProduct, IProductGroup, IProductTab, productGroupSorting } from '@models/product';
import { IReaggregationStatusItem } from '@models/Reaggregation/reaggregation-status-item';
import { ReaggregationType } from '@models/Reaggregation/reaggregation-type';
import { ProductStatus } from '@models/record-status';
import { EmpyreanApplicationName, isUserLevelForApplication } from '@models/user-level';
import { PreventableEvent } from '@progress/kendo-angular-dateinputs';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { routes } from '@routes/routes';
import { OpenIDConnectService } from '@services/auth/openid-connect.service';
import { ExportAlmService } from '@services/export/export-alm.service';
import { ExportService } from '@services/export/export.service';
import { ObservationDatesService } from '@services/global/observation-dates.service';
import { ProductReaggregationService } from '@services/global/product-reaggregation.service';
import { ProductService } from '@services/global/product.service';
import { ToastService } from '@services/global/toast.service';
import { ImportService } from '@services/import/import.service';
import { MenuService } from '@services/menu.service';
import { RecalculationService } from '@services/recalculation/recalculation.service';
import { StoreService } from '@services/store/store.service';
import { UserService } from '@services/user.service';
import { getDateString, getLastDayOfMonth } from '@shared/dates';
import { numberToString } from '@shared/numbers';
import { toasterConfig } from '@shared/toasterSetup';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('quickLinks', { static: false }) quickLinks: ElementRef;
  @ViewChild('userPanel', { static: false }) userPanel: ElementRef;

  public useAzure = false;
  public importSettingsPromise: Promise<IImportSettings>;

  public menuForm = new FormGroup({
    selectedOption: new FormControl(),
    selectedProduct: new FormControl()
  });

  private readonly dialogOptions = {
    height: 'calc(100% - 140px)',
    width: 'calc(100% - 140px)',
    position: { bottom: '' },
    maxWidth: '100vw',
    panelClass: ['ui-dialog-content'],
    disableClose: true,
    closeOnNavigation: false
  };
  private settingsDialogOpened = false;

  get isExportingCalibrations() {
    return this._isExportingCalibrations;
  }
  set isExportingCalibrations(value: boolean) {
    this._isExportingCalibrations = value;

    // Set Icon & Text for 'Export
    const isWaiting = value && this.isRecalculating;
    this.saveIconData[0].text = isWaiting
      ? 'Export Calibrations (waiting)'
      : value
      ? 'Building Calibrations Report ...'
      : 'Export Calibrations';
    this.saveIconData[0].icon = isWaiting ? 'clock' : value ? 'loading' : 'upload';
    this.saveIconData[0].disabled = value;
  }

  get isExportingALM() {
    return this._isExportingALM;
  }
  set isExportingALM(value: boolean) {
    this._isExportingALM = value;

    // Set Icon & Text for 'Export
    const isWaiting = value && this.isRecalculating;
    this.saveIconData[1].text = isWaiting
      ? 'Export ALM Input Sheets (waiting)'
      : value
      ? 'Building ALM Input Sheets ...'
      : 'Export ALM Input Sheets';
    this.saveIconData[1].icon = isWaiting ? 'clock' : value ? 'loading' : 'upload';
    this.saveIconData[1].disabled = value;
  }

  get isExporting() {
    return this.isExportingCalibrations || this.isExportingALM;
  }

  get isRecalculating() {
    return this._isRecalculating;
  }
  set isRecalculating(val: boolean) {
    this._isRecalculating = val;

    // Set Icon & Text for 'Export Calibrations' dropdown item
    const isWaitingCalibrations = val && this.isExportingCalibrations;
    this.saveIconData[0].text = isWaitingCalibrations
      ? 'Export Calibrations (waiting)'
      : this.isExportingCalibrations
      ? 'Building Calibrations Report ...'
      : 'Export Calibrations';
    this.saveIconData[0].icon = isWaitingCalibrations ? 'clock' : this.isExportingCalibrations ? 'loading' : 'upload';
    this.saveIconData[0].disabled = this.isExportingCalibrations;

    // Set Icon & Text for 'Export ALM' dropdown item
    const isWaitingAlm = val && this.isExportingALM;
    this.saveIconData[1].text = isWaitingAlm
      ? 'Export ALM Input Sheets (waiting)'
      : this.isExportingALM
      ? 'Building ALM Input Sheets ...'
      : 'Export ALM Input Sheets';
    this.saveIconData[1].icon = isWaitingAlm ? 'clock' : this.isExportingALM ? 'loading' : 'upload';
    this.saveIconData[1].disabled = this.isExportingALM;
  }

  public get EmpyreanApplicationName() {
    return EmpyreanApplicationName;
  }

  public get selectedProduct() {
    return this.menuForm.controls.selectedProduct.value;
  }

  public get selectedOption() {
    return this.menuForm.controls.selectedOption.value;
  }
  public get selectedProductTab() {
    return this.menuForm.controls.selectedProductTab.value;
  }
  public get maxUserLevel() {
    return this.menu.getMaxUserLevel();
  }

  get userAvailable() {
    return this.auth.userAvailable && !this.auth.user.expired;
  }

  get userTooltip() {
    return `${this.userName} [${this.auth.userId}]\nCustomer: ${this.auth.customerId}\n${this.maxUserLevel}`;
  }

  get userDetails() {
    return `${this.userName}<br/>${this.maxUserLevel}`;
  }

  get appBuildTooltip() {
    return `Deposit Analytics v${this.appVersion}
    ${this.userName} [${this.auth.userId}]
    ${this.maxUserLevel}
    Customer: ${this.auth.customerId}`;
  }

  public get exportReadyNotification(): string {
    let notification = null;

    if (this.isRecalculating) {
      notification = `Waiting for Recalculation ${this.recalcProgress}...`;
    } else {
      if (this.isExportingCalibrations || this.isExportingALM) {
        if (this.isExportingCalibrations && this.isExportingALM) {
          notification = 'Building Calibrations Reports and ALM Input Sheets ...';
        } else if (this.isExportingCalibrations) {
          notification = 'Building Calibrations Reports ...';
        } else if (this.isExportingALM) {
          notification = 'Building ALM Input Sheets ...';
        }
      } else if (this.saveIconData[0].isExportReady && this.saveIconData[1].isExportReady) {
        notification = 'Calibrations Reports and ALM Input Sheets are Ready';
      } else if (this.saveIconData[0].isExportReady) {
        notification = 'Calibrations Reports are Ready';
      } else if (this.saveIconData[1].isExportReady) {
        notification = 'ALM Input Sheets are Ready';
      }
    }

    return notification;
  }

  public get exportIcon(): string {
    if (this.isRecalculating) {
      return 'clock';
    }
    if (this.isExporting) {
      return 'loading';
    }
    return 'upload';
  }

  private _isExportingALM = false;
  private _isExportingCalibrations = false;
  private _isRecalculating = false;
  private dialogRef: MatDialogRef<ExportALMConfigComponent | ExportConfigComponent>;
  private productTabs: IProductTab[] = [];
  private readonly concentrationReportOption = MenuOptions.getOptionByPath('concentration');
  private readonly currentMonthOption = MenuOptions.getOptionByPath('current-period');
  private readonly destroyed$ = new Subject();
  private readonly exportALMOption = MenuOptions.getOptionByPath('export-alm');
  private readonly exportCalibrationsOption = MenuOptions.getOptionByPath('export-preview');
  private subscriptions: Subscription[] = [];
  private userName = '';

  public appVersion: string;
  public disabledProductIds: number[] = [];
  public dropDownList: IProductMenuOption[] | GroupResult[] = [];
  public dropDownList2: IProductMenuOption[] | GroupResult[] = [];
  public exportConfigModalOpened = false;
  public exportPreviewOpened = false;
  public lastObservationDate: Date = null;
  public moduleUrls: IAppConfigModules;
  public numberOfProducts: number = null;
  public optionList: IMenuOption[] | GroupResult[] = MenuOptions.All();
  public productList: IProduct[] | GroupResult[] = [allProductsItem];
  public productOptionList: IProductMenuOption[] | GroupResult[] = [];
  public productsDisabledByAcquisitionReaggregation: number[] = [];
  public quickLinksOpen = false;
  public readonly exportDialogSizes = exportDialogSizes;
  public recalcProgress = '';
  public showImportDialog = false;
  public toasterConfig = toasterConfig;
  public userAreaOpen = false;

  public saveIconData: Array<any> = [
    {
      //
      //  Export Calibrations
      //

      isExportReady: false,
      click: () => {
        this.dialogRef = this.dialog.open(ExportConfigComponent, {
          ...this.dialogOptions,
          data: { products: this.productList }
        });

        this.subscriptions.push(
          this.dialogRef.afterClosed().subscribe((result: any) => {
            this.unsubscribe();
            if (result.BuildReportClicked) {
              const productIds = result.products.map(p => p.ProductId).sort((a, b) => a - b);
              const fixedTabIds = result.fixedTabs.map(x => x.id);
              this.startCalibrationExportAfterRecalculation(productIds, fixedTabIds);
            }
          })
        );

        this.subscriptions.push(
          this.dialogRef.keydownEvents().subscribe(e => {
            if (e.key === 'Escape') {
              this.dialogRef.close(false);
            }
          })
        );
      }
    },
    {
      //
      //  Export ALM
      //

      isExportReady: false,
      click: () => {
        this.dialogRef = this.dialog.open(ExportALMConfigComponent, {
          ...this.dialogOptions,
          data: { productTabs: this.productTabs }
        });

        this.subscriptions.push(
          this.dialogRef.afterClosed().subscribe((request: IExportAlmRequest) => {
            this.unsubscribe();
            if (request.BuildReportClicked) {
              this.startAlmExportAfterRecalculation(request);
            }
          })
        );

        this.subscriptions.push(
          this.dialogRef.keydownEvents().subscribe(e => {
            if (e.key === 'Escape') {
              this.dialogRef.close(false);
            }
          })
        );
      }
    }
  ];

  /**
   * Returns number of unfinished tasks currently in the Recalculation queue
   */
  private async getUnfinishedTasks(productIds: number[]) {
    let tasks = (await this.recalculationService.getRecalculationTasks().toPromise()) || [];

    // 1. First, remove any product NOT in the input productIds (e.g. not being exported)
    tasks = tasks.filter(t => productIds.includes(t.ProductId));

    // 2. Remove all fully completed requests
    const requestIds = Array.from(new Set(tasks.map(t => t.RequestId)));
    requestIds.forEach(requestId => {
      const requestTasks = tasks.filter(t => t.RequestId === requestId);
      if (requestTasks.every(t => t.EndDateTime)) {
        tasks = tasks.filter(t => t.RequestId !== requestId);
      }
    });

    const unfinishedTasks = tasks.filter(t => !t.HasError && !t.EndDateTime);

    if (unfinishedTasks.length > 0) {
      this.isRecalculating = true;
      this.recalcProgress = `(${tasks.length - unfinishedTasks.length}/${tasks.length}) `;
    } else {
      this.isRecalculating = false;
      this.recalcProgress = '';
    }
    return unfinishedTasks.length;
  }

  /**
   * Waits until there is no Recalculation in progress, then starts ALM Export
   */
  private async startAlmExportAfterRecalculation(request: IExportAlmRequest) {
    // If the user is currently in the "Export ALM" page, navigate to Backtest Report (home) before creating a new Export.
    const currentPath = this.menuForm.value.selectedOption.path;
    const navigationPromise = currentPath === 'export-alm' ? this.router.navigate(['backtest']) : Promise.resolve(true);
    const unfinishedTasksPromise = this.getUnfinishedTasks(request.ProductIds);
    const res = await Promise.all([navigationPromise, unfinishedTasksPromise]);
    const unfinishedTasks = res[1];

    this.isExportingALM = true;
    this.exportAlmService.setExportAlmMenuOption(true);

    if (unfinishedTasks > 0) {
      setTimeout(
        () => {
          this.startAlmExportAfterRecalculation(request);
        },
        unfinishedTasks > 5 ? 10000 : 5000
      );
    } else {
      this.startAlmExport(request);
    }
  }

  /**
   * Waits until there is no Recalculation in progress, then starts ALM Export
   */
  private async startCalibrationExportAfterRecalculation(productIds: number[], fixedTabIds: string[]) {
    // If the user is currently in the "Export Preview (Calibrations)" page, navigate to Backtest Report
    // (home) before creating a new Export.
    const currentPath = this.menuForm.value.selectedOption.path;
    const navigationPromise = currentPath === 'export-preview' ? this.router.navigate(['backtest']) : Promise.resolve(true);
    const unfinishedTasksPromise = this.getUnfinishedTasks(productIds);
    const res = await Promise.all([navigationPromise, unfinishedTasksPromise]);
    const unfinishedTasks = res[1];

    this.isExportingCalibrations = true;
    this.exportCalibrationsOption.disabled = true;

    if (unfinishedTasks > 0) {
      setTimeout(
        () => {
          this.startCalibrationExportAfterRecalculation(productIds, fixedTabIds);
        },
        unfinishedTasks > 5 ? 10000 : 5000
      );
    } else {
      this.startCalibrationExport(productIds, fixedTabIds);
    }
  }

  private startAlmExport(request: IExportAlmRequest) {
    if (request.ExportMethod === ExportAlmMethod.NonMaturity) {
      this.exportAlmService
        .exportNonMaturity(request)
        .toPromise()
        .then((response: IExportAlmNonMaturityResponse) => {
          this.saveIconData[1].isExportReady = true;
          this.exportAlmService.setExportAlmMenuOption(false);

          this.toastService.showToast({
            title: 'Export Finished',
            body: `ALM Input Sheets are Ready<br/>(Non Maturity)`,
            type: 'success',
            showCloseButton: true
          });
        })
        .catch(err => {
          this.toastService.showToast({
            title: 'Export Failed',
            body: `Error building ALM Input Sheet<br/>(Non Maturity)`,
            type: 'error',
            showCloseButton: true
          });
        })
        .finally(() => {
          this.isExportingALM = false;
        });
    } else {
      // @ts-ignore
      request.AsOfDate = getDateString(getLastDayOfMonth(request.AsOfDate));

      this.exportAlmService
        .exportVintage(request)
        .toPromise()
        .then((response: IExportAlmVintageResponse) => {
          this.saveIconData[1].isExportReady = true;
          this.exportAlmService.setExportAlmMenuOption(false);

          // Save product mapping after successful export
          const saveMappingResult = this.exportAlmService.saveProductMapping();
          if (saveMappingResult) {
            saveMappingResult.toPromise().catch(() => {
              // Silently fail - product mapping save is not critical
            });
          }

          this.toastService.showToast({
            title: 'Export Finished',
            body: `ALM Input Sheets are Ready (Vintage)<br/>${numberToString(response.NumberOfRows, 1)} CP records`,
            type: 'success',
            showCloseButton: true
          });
        })
        .catch(err => {
          console.warn(err);
          this.toastService.showToast({
            title: 'Export Failed',
            body: 'Error building ALM Input Sheets (Vintage)',
            type: 'error',
            showCloseButton: true
          });
        })
        .finally(() => {
          this.isExportingALM = false;
        });
    }
  }

  private startCalibrationExport(productIds: number[], fixedTabIds: string[]) {
    this.buildExportPreview(productIds, fixedTabIds)
      .then(response => {
        if (response) {
          this.saveIconData[0].isExportReady = true;
          this.isExportingCalibrations = false;
          this.exportCalibrationsOption.disabled = false;
          this.toastService.showToast({
            title: 'Export Finished',
            body: 'Calibrations Reports are Ready',
            type: 'success',
            showCloseButton: true
          });
        } else {
          this.saveIconData[0].isExportReady = false;
          this.isExportingCalibrations = false;
          this.exportCalibrationsOption.disabled = true;
        }
      })
      .catch(err => {
        this.isExportingCalibrations = false;
        console.warn(`Error building Calibrations Report:\n${err.stack}`);
        this.toastService.showToast({
          title: 'Export Failed',
          body: 'Error building Calibrations Reports',
          type: 'error',
          showCloseButton: true
        });
      });
  }

  public get buildingExportPreview(): boolean {
    return this.exportService.buildingExportPreview;
  }
  public get noExportData(): boolean {
    return (
      !this.exportService.exportData || !this.exportService.exportData.ExportItems || this.exportService.exportData.ExportItems.length === 0
    );
  }

  public get exportRetrievingData(): boolean {
    return this.exportService.retrievingDataIds.length > 0;
  }

  constructor(
    private readonly appConfig: AppConfigProvider,
    private readonly auth: OpenIDConnectService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly exportAlmService: ExportAlmService,
    private readonly importService: ImportService,
    private readonly observationDatesService: ObservationDatesService,
    private readonly productReaggregationService: ProductReaggregationService,
    private readonly productService: ProductService,
    private readonly recalculationService: RecalculationService,
    private readonly router: Router,
    private readonly store: StoreService,
    private readonly toastService: ToastService,
    private readonly userService: UserService,
    public readonly dialog: MatDialog,
    public readonly exportService: ExportService,
    public readonly importDialog: MatDialog,
    public readonly menu: MenuService
  ) {
    this.importSettingsPromise = this.importService.getImportSettings();
    this.importSettingsPromise.then(res => {
      this.useAzure = res.Use_Azure;
    });

    this.userService.getUserName(this.auth.userId).then(userName => (this.userName = userName));

    this.appConfig.environment.subscribe(env => {
      this.appVersion = env.VERSION.BUILDNUMBER;
      this.moduleUrls = env.MODULES;
    });

    this.recalculationService.pollImportRecalculation();

    this.isExportingCalibrations = false;
    this.isExportingALM = false;

    // Reloading list for dropdown to get any changes on Product Setup
    this.subscriptions.push(
      this.menu.getHeaderVal().subscribe(async res => {
        if (res) {
          // this.productOptionList = [];
          await this.loadData();
          setTimeout(() => {
            const product = this.menu.selectedProduct;
            const tab = this.menu.selectedTab;
            this.onNavigationEnd();
          }, 100);
        }
      })
    );
  }

  private unsubscribe() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  public isDepositsAdmin = () => this.menu.isDepositsAdmin();
  public isDepositsUser = () => this.menu.isDepositsUser();

  public isUrlAvailable(moduleName: EmpyreanApplicationName): boolean {
    return this.moduleUrls.hasOwnProperty(moduleName) && this.moduleUrls[moduleName] !== '';
  }

  public isApplicationAvailableToUser(moduleName: EmpyreanApplicationName): boolean {
    return isUserLevelForApplication(moduleName, this.auth.userLevelList);
  }

  public isApplicationSwitcherEnabled(moduleName: EmpyreanApplicationName): boolean {
    return this.isApplicationAvailableToUser(moduleName) && this.isUrlAvailable(moduleName);
  }

  public applicationSwitcherTooltip(moduleName: EmpyreanApplicationName): string | null {
    if (!this.isUrlAvailable(moduleName)) {
      return 'This module is not available, please contact your Empyrean representative for more information.';
    }
    if (!this.isApplicationAvailableToUser(moduleName)) {
      return 'You are not authorized to access this module.';
    }

    return null;
  }

  ngOnInit(): void {
    this.productList = groupBy([allProductsItem], [{ field: 'ProductGroupName' }]);
    this.optionList = MenuOptions.All();

    if (!this.menu) {
      return;
    }
    this.menuForm.controls.selectedOption.setValue(MenuOptions.getOptionByPath('backtest'), { emitEvent: true });

    this.hideDropdownOnClickOutside();

    // If we have a last observation date (e.g. product time series on DB is non-empty), we enable menu
    // options for 'Concentration Report' and 'Current Month Summary'
    this.observationDatesService
      .getLastObservationDate()
      .pipe(
        map(lastObservationDate => {
          this.lastObservationDate = lastObservationDate;
          this.concentrationReportOption.disabled = !lastObservationDate;
          this.currentMonthOption.disabled = !lastObservationDate;

          return lastObservationDate;
        })
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe();

    this.exportAlmService.exportAlmMenuOptionDisabled$.pipe(takeUntil(this.destroyed$)).subscribe(state => {
      this.exportALMOption.disabled = state;
    });

    this.loadData();
  }
  ngAfterViewInit() {
    this.initSubscriptions();
  }
  public ngOnDestroy(): void {
    // Unsubscribe observables:
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private loadData() {
    return forkJoin([this.productService.getProducts(), this.productService.getProductGroups(), this.productService.getProductTabs()])
      .toPromise()
      .then(res => {
        const [products, groups, tabs] = res;
        this.productOptionList = [];
        this.numberOfProducts = products.length;
        const activeProducts = products.filter(prod => prod.RecordStatus === ProductStatus.Active);

        this.productList = groupBy([allProductsItem, ...activeProducts], [{ field: 'ProductGroupName' }]).sort((a, b) =>
          productGroupSorting(a as IProductGroup, b as IProductGroup)
        );

        // -- Sort the products within each group (items)
        this.productList.forEach(g => {
          if (g.item) {
            applyProductSortingRules(g.item);
          }
        });

        // Ensuring all is always at the top
        // @ts-ignore
        if (this.productList[0].value !== 'ALL') {
          const allGroupIndex = this.productList.findIndex(g => g.value === 'ALL');
          if (allGroupIndex >= 0) {
            const allProducts = this.productList[allGroupIndex];
            // @ts-ignore
            this.productList = this.productList.filter(g => g.value !== 'ALL');
            // @ts-ignore
            this.productList.unshift(allProducts);
          }
        }

        // const activeProductOptions = [];

        let optionListId = 0;
        const productMenuOptions = activeProducts.map(p => {
          return {
            disabled: false,
            tabId: p.TabId,
            group: p.ProductGroupName,
            id: p.ProductId,
            label: p.ProductName,
            path: p.ProductName,
            type: 'product',
            pId: optionListId += 1
          };
        });

        const tabsGroup = {
          aggregates: {},
          field: 'group',
          // tabId: 0,
          items: [
            {
              disabled: false,
              group: '',
              id: -1,
              label: 'All Products',
              path: 'All Products',
              type: 'tab',
              pId: null
            }
          ],
          value: 'Tabs'
        };

        tabs.forEach(tab => {
          // tabsGroup.tabId = tab.Id;
          tabsGroup.items.push({
            disabled: false,
            group: '',
            id: tab.Id,
            label: tab.Name,
            path: tab.Name,
            type: 'tab',
            pId: optionListId += 1
          });
        });

        // @ts-ignore
        this.productOptionList.push(tabsGroup);

        groups
          .filter(({ IsActive }) => IsActive)
          .forEach(group => {
            // @ts-ignore
            this.productOptionList.push({
              aggregates: {},
              field: 'group',
              items: productMenuOptions.filter(prod => prod.group === group.GroupName),
              value: group.GroupName
            });
          });

        // @ts-ignore
        this.productOptionList = this.productOptionList.filter(x => x.items.length > 0);
        const opt = this.productOptionList[0];
        // @ts-ignore
        this.dropDownList2 = this.productOptionList.slice(1);
        // @ts-ignore
        const trip = opt.items;
        this.dropDownList = trip;
        this.productTabs = tabs;
        const publishedTab = tabs.find(({ IsPublished }) => IsPublished);
        this.menu.selectedProductTab = publishedTab;
        this.selectOption(this.selectedOption);
      });
  }

  public initSubscriptions() {
    this.onReaggregationStatusCheck();

    this.dialog.afterOpened.subscribe(res => (this.settingsDialogOpened = true));
    this.dialog.afterAllClosed.subscribe(res => (this.settingsDialogOpened = false));

    this.router.events.pipe(filter(event => event instanceof GuardsCheckEnd)).subscribe((event: GuardsCheckEnd) => {
      if (!event.shouldActivate) {
        // Unable to activate - Reverting the selected Option
        const product = this.menu.selectedProduct;
        this.menuForm.controls.selectedProduct.setValue(
          {
            disabled: false,
            group: product.ProductGroupName,
            id: product.ProductId,
            label: product.ProductName,
            path: product.ProductName,
            type: 'product'
          },
          { emitEvent: false }
        );
        this.changeDetector.detectChanges();
      }
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      // Once route is successfully navigated we can set the product and the selected option in the menu service
      this.onNavigationEnd();
    });

    // this.router.events.subscribe(res => {console.log(res)})
  }

  public async selectProduct(product) {
    this.onProductSelected(product);
  }

  public async selectOption(option) {
    this.onOptionSelected(option);
  }

  public onOptionSelected(option: IMenuOption) {
    if (option.group === 'product') {
      // Product Navigation
      const route = routes.find(r => r.path === option.path);
      if (route && this.auth.userAvailable) {
        this.router.navigate([route.path, this.menu.selectedProduct.ProductId], {}).catch(err => {
          console.error(err);
          this.menuForm.controls.selectedOption.setValue(this.selectedOption, { emitEvent: false });
        });
      }
    } else {
      // All Product options navigation

      const route = routes.find(r => r.path === option.path);
      let routePath;
      if (route.path === 'export-preview' || route.path === 'export-alm') {
        routePath = [route.path];
      } else {
        routePath = [route.path, this.menu.selectedProductTab ? this.menu.selectedProductTab.Id : -1];
      }

      if (route && this.auth.userAvailable) {
        this.router
          .navigate(routePath, {})
          .catch(err => {
            // ,this.menu.selectedProductTab.Id
            console.error(err);
            this.menuForm.controls.selectedOption.setValue(this.selectedOption, { emitEvent: false });
          })
          .then(result => {
            if (result) {
              if (option.path === 'export-preview') {
                this.saveIconData[0].isExportReady = false;
              } else if (option.path === 'export-alm') {
                this.saveIconData[1].isExportReady = false;
              }
            }
          })
          .catch(err => {
            console.error(err);
            this.menuForm.controls.selectedOption.setValue(this.selectedOption, { emitEvent: false });
          });
      }
    }
  }

  private onProductSelected(opt: IProductMenuOption) {
    if (opt.type === 'tab') {
      // this.menu.selectedProductTab$.next(option)
      if (this.selectedOption.group !== 'all') {
        this.optionList = MenuOptions.All();
      }
      // @ts-ignore
      const option = this.optionList.find(o => !o.disabled);
      // @ts-ignore
      const option2 = this.optionList.find(o => o.path === this.selectedOption.path);
      if (this.selectedOption.group === 'product') {
        this.router
          .navigate(['backtest', opt.id])
          .then(res => {})
          .catch(err => {
            console.error(err);
          });
      } else {
        this.router
          .navigate([option2.path, opt.id])
          .then(res => {})
          .catch(err => {
            console.error(err);
          });
      }
    } else {
      if (this.selectedOption.group !== 'product') {
        this.optionList = groupBy(MenuOptions.Product(), [{ field: 'subgroup' }]);
        const option = MenuOptions.getOptionByLabel('Dashboard');
        this.router.navigate([option.path, opt.id]).then(res => {});
        this.changeDetector.detectChanges();
      } else {
        this.router.navigate([this.menu.selectedOption.path, opt.id]).then(res => {});
        this.changeDetector.detectChanges();
      }
    }
  }

  public onClose(event) {
    event.preventDefault();
  }

  public logout() {
    this.auth.triggerSignOut();
  }

  public onDropdownOpen(event) {
    this.onReaggregationStatusCheck();
  }

  public onDropdownClose(event) {
    event.preventDefault();
  }

  private onReaggregationStatusCheck() {
    const pollTime = 5000;
    const polling$ = this.productReaggregationService.getProductReaggregationStatusPolling(pollTime).pipe(takeUntil(this.destroyed$));

    polling$.subscribe(reaggStatusItems => {
      const hiddenStatusList = this.productReaggregationService.unfinishedReaggregationList;
      const failedItems = reaggStatusItems.filter(item => item.HasError);

      this.disabledProductIds = reaggStatusItems
        .filter(
          (item: IReaggregationStatusItem) =>
            // Hidden Status
            (hiddenStatusList.includes(item.ReaggregationStatus) ||
              // Completed with Errors
              failedItems.find(failed => item.ProductID === failed.ProductID)) &&
            // Not Acquisition Reaggregation
            item.ReaggregationType !== ReaggregationType.Acquisition
        )
        .map(item => item.ProductID);

      this.productsDisabledByAcquisitionReaggregation = reaggStatusItems
        .filter(
          (item: IReaggregationStatusItem) =>
            // Hidden Status
            (hiddenStatusList.includes(item.ReaggregationStatus) ||
              // Completed with Errors
              failedItems.find(failed => item.ProductID === failed.ProductID)) &&
            // Acquisition Reaggregation
            item.ReaggregationType === ReaggregationType.Acquisition
        )
        .map(item => item.ProductID);

      // Subgroup 3 (Calibrations)
      const options: IMenuOption[] = (this.optionList[2] as GroupResult).items as IMenuOption[];

      if (options) {
        options.forEach(option => {
          option.disabled =
            (option.subgroup === 3 && this.disabledProductIds.includes(this.selectedProduct.ProductId)) ||
            (option.subgroup === 3 &&
              option.path === 'decay' &&
              this.productsDisabledByAcquisitionReaggregation.includes(this.selectedProduct.ProductId));
        });
      }
      // If there is some item flagged for reaggregation, auto-start reaggregation
      this.productReaggregationService.autoReaggregateIfRequired();
    });
  }

  public itemDisabled(itemArgs: { dataItem: IMenuOption; index: number }) {
    return itemArgs.dataItem.disabled;
  }

  public isProductDisabled() {
    return (
      this.disabledProductIds.includes(this.selectedProduct.ProductId) ||
      this.productsDisabledByAcquisitionReaggregation.includes(this.selectedProduct.ProductId)
    );
  }

  public async buildExportPreview(productIds: number[], fixedTabIds: string[]) {
    this.exportService.buildingExportPreview = true;

    const exportFilterType = this.exportService.exportFilterType;

    this.exportService.resetValues();
    this.exportService.fixedTabs.filter(tab => fixedTabIds.includes(tab.id)).forEach(tab => (tab.IsChecked = true));

    this.exportService.exportFilterType = exportFilterType;

    const allProductIds = this.store.state.products
      .filter(prod => prod.RecordStatus === ProductStatus.Active)
      .map(({ ProductId }) => ProductId)
      .sort((a, b) => a - b);

    await this.exportService.loadExportEssentialData(allProductIds);
    await this.exportService.loadExportComplementaryData(productIds, fixedTabIds);

    return await this.exportService.buildExportWorkbook(productIds, true);
  }

  public navigateToExport() {
    if (this.saveIconData[0].isExportReady) {
      if (this.exportCalibrationsOption) {
        this.selectOption(this.exportCalibrationsOption);
      }
    } else if (this.saveIconData[1].isExportReady) {
      if (this.exportALMOption) {
        this.selectOption(this.exportALMOption);
      }
    }
  }

  private hideDropdownOnClickOutside() {
    const outsideClickListener = event => {
      const inside = this.quickLinks.nativeElement.contains(event.target) || this.userPanel.nativeElement.contains(event.target);
      if (!inside) {
        this.closeAllDropdowns();
      }
    };

    document.addEventListener('click', outsideClickListener);
  }

  public closeAllDropdowns() {
    this.quickLinksOpen = false;
    this.userAreaOpen = false;
  }

  public toggleUserArea() {
    this.userAreaOpen = !this.userAreaOpen;
    if (this.userAreaOpen) {
      this.quickLinksOpen = false;
    }
  }

  public toggleQuickLinks() {
    this.quickLinksOpen = !this.quickLinksOpen;
    if (this.quickLinksOpen) {
      this.userAreaOpen = false;
    }
  }

  public openImport() {
    this.showImportDialog = !this.showImportDialog;
  }

  public exportClose(event: PreventableEvent) {
    // event.preventDefault();
  }

  private onNavigationEnd() {
    const route = this.router.url;

    if (route === '/') {
      this.router.navigate(['backtest']);
      return;
    }

    const urlSplit = route.split('/');
    if (urlSplit && urlSplit.length > 0 && !this.settingsDialogOpened) {
      const allOptions = MenuOptions.All().concat(MenuOptions.Product());

      let allProducts: IProduct[] = [];
      this.productList.forEach(pr => {
        allProducts = allProducts.concat(pr.items);
      });

      const option = allOptions.find(({ path }) => path === urlSplit[1]);
      if (option) {
        if (option.group === 'all') {
          // Change Options
          this.optionList = MenuOptions.All();
          // Assign All Product Option
          const product = allProducts.find(pr => pr.ProductName === 'All Products');

          this.menu.selectedOption = option;

          this.menuForm.controls.selectedOption.setValue(option, { emitEvent: false });
          if (urlSplit.length > 1) {
            // @ts-ignore
            const prod = this.dropDownList.find(({ id }) => id === Number(urlSplit[2]));
            const tab = this.productTabs.find(({ Id }) => Id === Number(urlSplit[2]));
            if (tab) {
              this.menuForm.controls.selectedProduct.setValue(
                {
                  id: tab.Id,
                  type: 'tab',
                  path: tab.Name,
                  label: tab.Name,
                  disabled: false,
                  group: '',
                  pId: prod && prod.pId ? prod.pId : tab.Id
                },
                { emitEvent: false }
              );
              this.menu.selectedProductTab = tab;
            } else {
              // User Selected All Products
              this.menu.selectedProductTab = null;

              this.menuForm.controls.selectedProduct.setValue(
                { disabled: false, group: '', id: -1, label: 'All Products', path: 'All Products', type: 'tab' },
                { emitEvent: false }
              );
            }
          } else {
            this.menu.selectedProductTab = null;
            this.menuForm.controls.selectedProduct.setValue(product, { emitEvent: false });
          }
          this.menu.closeDialog();
        } else if (option.group === 'product') {
          // Now we need to find the selected product
          const product = allProducts.find(({ ProductId }) => Number(urlSplit[2]) === ProductId);
          let optionItem = null;
          // @ts-ignore
          this.dropDownList2.forEach(x => {
            x.items.forEach(y => {
              if (y.id === Number(urlSplit[2])) {
                optionItem = y;
              }
            });
          });
          if (product) {
            this.optionList = MenuOptions.Product();
            // Assign selected Product and selected option for menu service
            this.menu.selectedProduct = product;
            this.menu.selectedOption = option;

            this.menuForm.controls.selectedOption.setValue(option, { emitEvent: false });
            this.menuForm.controls.selectedProduct.setValue(
              {
                disabled: false,
                group: product.ProductGroupName,
                id: product.ProductId,
                label: product.ProductName,
                path: product.ProductName,
                type: 'product',
                pId: optionItem.pId
              },
              { emitEvent: false }
            );
            this.menu.closeDialog();
          }
        }
      }
    }
  }
}
